import { MutableRefObject, useCallback, useEffect, useRef } from 'react';

import * as arbitraryEvents from 'js/core_webpack/arbitrary_events';
import useFullstory from 'js/utils/hooks/useFullstory';

// Per-type suffix for the FullStory init event name. Type-specific names
// make it easy to search FS dashboards / jump to the moment a user opened
// a given modal in a recording (e.g. `…ReadersExportModalOpened`).
function fsModalSuffix(exportType: string): string {
  switch (exportType) {
    case 'mentions':
      return 'MentionsExportModalOpened';
    case 'readers':
      return 'ReadersExportModalOpened';
    case 'profile_visitors':
      return 'ProfileVisitorsExportModalOpened';
    case 'bulk_download_packs':
      return 'BulkDownloadPacksExportModalOpened';
    default:
      return 'ExportModalOpened';
  }
}

interface UseExportFunnelTrackingOptions {
  // 'mentions' | 'readers' | 'profile_visitors' | 'bulk_download_packs'
  exportType: string;
  // The number of records being exported (or the number of packs, for the
  // bulk-download flow). Passed via ref internally so callers can update it
  // mid-lifecycle — e.g. the bulk-download modal only knows the pack count
  // after its workIds fetch resolves.
  recordCount: number;
}

interface UseExportFunnelTrackingResult {
  // Fire a `PremiumDataExportFunnel` arbitrary event with the given action.
  // `export_type` and `record_count` are merged in automatically; `extra`
  // overrides when keys collide.
  fireEvent: (
    action: string,
    extra?: Partial<{
      format: string;
      pack_index: number;
      next_pack_index: number;
      pack_record_count: number;
      pack_unavailable: boolean;
      completed_packs: number;
      last_step: string;
      phase: string;
      record_count: number;
    }>,
  ) => void;
  // Caller-controlled flag for "the user reached `done`". Used to suppress
  // the `abandoned` event when the modal closes after a successful
  // completion. Set `current = true` when the user reaches the done step.
  completedRef: MutableRefObject<boolean>;
}

// Shared funnel tracking for the per-type export modals
// (ExportFormatModal + BulkDownloadPackExportModal). Handles:
//
//   * FullStory init at 100% sampling, purely for session-recording context
//     (UX spot-checking). FS does NOT receive analytics events — those go
//     to the `PremiumDataExportFunnel` arbitrary event and land in Redshift.
//   * An `opened` action fired on mount, with `export_type` and
//     `record_count` populated. **Use `opened` (not the bare FS init event)
//     as the funnel-entry event in analytics queries.**
//   * A `fireEvent` helper that wraps `arbitraryEvents.write` and
//     auto-merges `export_type` + `record_count` into every event.
//   * A `completedRef` flag for the standard "don't fire `abandoned` after a
//     successful close" pattern.
export default function useExportFunnelTracking({
  exportType,
  recordCount,
}: UseExportFunnelTrackingOptions): UseExportFunnelTrackingResult {
  // FullStory is initialized at 100% sampling and fires a type-specific
  // `*ExportModalOpened` event as a session-replay marker — the team uses
  // it to jump to the moment a user opens a given export modal in a
  // recording. Funnel analytics goes through arbitrary events below.
  useFullstory(`PremiumDataExport.${fsModalSuffix(exportType)}`, 1);

  // Mirror recordCount into a ref so fireEvent's identity stays stable as
  // the count updates (e.g. when the bulk-download modal learns its pack
  // count post-fetch).
  const recordCountRef = useRef(recordCount);
  recordCountRef.current = recordCount;

  const fireEvent = useCallback<UseExportFunnelTrackingResult['fireEvent']>(
    (action, extra) =>
      arbitraryEvents.write('PremiumDataExportFunnel', {
        action,
        export_type: exportType,
        record_count: recordCountRef.current,
        ...(extra ?? {}),
      }),
    [exportType],
  );

  const openedFiredRef = useRef(false);
  useEffect(() => {
    if (openedFiredRef.current) return;
    openedFiredRef.current = true;
    fireEvent('opened');
    // Fire once on mount; deps would re-fire if fireEvent reidentified.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const completedRef = useRef(false);

  return { fireEvent, completedRef };
}
